import { useRef, useState } from "react";
import { useUI } from "../store/ui";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { PERSONAS, ALL_DEPTS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";
import { generateAgendaAIStream, extractTasksAI, suggestMeetingAttendeesAI } from "../lib/claude";
import { MeetingPoster } from "./MeetingPoster";
import { snapshotElement } from "../lib/snapshot";
import { useTasks } from "../store/tasks";
import { clientById, CLIENTS } from "../data/clients";
import { MeetingTaskBuilder } from "./MeetingTaskBuilder";
import { MeetingBrainstorm } from "./MeetingBrainstorm";

const ATA_LINE = (id: string): string | undefined => {
  const lines: Record<string, string> = {
    trump:    "Big league. Vamos ser HUGE — ou ficamos quietos.",
    marcio:   "Donald, escuta — esse plano só fecha se o ROI passar de 3x.",
    flavio:   "Sem brand, a gente vira commodity. Cuidem da marca.",
    lemann:   "Corte 20% do orçamento e refaça as metas. Eficiência.",
    musk:     "Por que não fazemos o dobro em metade do tempo?",
    bezos:    "Comece pelo cliente e trabalhe pra trás. Sempre.",
    hormozi:  "A oferta tá fraca. Empilha mais bônus e dobra o preço.",
    schwartz: "Onde está a consciência do mercado? É 4 ou 5?",
    ogilvy:   "If it doesn't sell, it isn't creative.",
    halbert:  "Quem é o avatar? Sem ele, copy é poesia inútil.",
    deakins:  "A luz tá errada nessa cena. Refaz no golden hour.",
    nolan:    "Sem CGI. Filma de verdade. Em IMAX.",
    sobral:   "ROAS abaixo de 2 a gente mata. Próxima criação.",
    belfort:  "Tá pronto pra fechar? Posiciona, posiciona, posiciona.",
    buffett:  "Be fearful when others are greedy. Greedy when others are fearful.",
    harvey:   "I don't have dreams, I have goals. Próxima cláusula."
  };
  return lines[id];
};

const CONSELHO_LINE = (id: string, topic: string): string => {
  const map: Record<string, string> = {
    trump:    `Marcio, isso aqui é vencedor. Mas você precisa fechar mais rápido — eu já assinaria.`,
    marcio:   `Marcio, mantenha a calma. Olhe os números antes de qualquer "sim". Esse "${topic}" depende do CAC abaixo de X.`,
    flavio:   `Marcio, o nome da marca está na frente disso? Se não está, recue.`,
    lemann:   `Marcio, qual o ROI projetado? Sem ROI claro, não autorize.`,
    musk:     `Marcio, faça pela metade do prazo e dobre a ambição. O resto resolve no caminho.`,
    bezos:    `Marcio, o cliente final ganha algo com isso? Se sim, prossiga. Se for só margem, repense.`,
    hormozi:  `Marcio, transforme isso numa oferta irrecusável: empilhe bônus, garantia 2x, escassez real.`,
    schwartz: `Marcio, em que nível de consciência está seu mercado? Use a copy certa pra ele.`,
    ogilvy:   `Marcio, headline primeiro. Se não vende em 5 palavras, refaça.`,
    halbert:  `Marcio, esquece a estratégia bonita. Quem compra e por quê? Volte aí.`,
    deakins:  `Marcio, sem direção de luz, o vídeo não traduz. Investe em DP de verdade.`,
    nolan:    `Marcio, escolhe escala. Filme grande ou não filme.`,
    sobral:   `Marcio, mete teste A/B antes de qualquer decisão. Sem dado, é palpite.`,
    belfort:  `Marcio, o pitch tem que ter Tom, Tempo e Linha Reta. Treine o time.`,
    buffett:  `Marcio, paciência. Não invista pesado se você não entende o ativo.`,
    harvey:   `Marcio, negocie de posição forte. Saia da mesa duas vezes antes de fechar.`
  };
  return map[id] ?? `Marcio, peço que considere o ângulo de ${PERSONAS.find(p => p.id === id)?.name} sobre "${topic}".`;
};

export function MeetingRoom() {
  const picks      = useUI(s => s.meetingPicks);
  const toggle     = useUI(s => s.toggleMeeting);
  const clearAll   = useUI(s => s.clearMeeting);
  const mode       = useUI(s => s.agendaMode);
  const setMode    = useUI(s => s.setAgendaMode);
  const saveMtg    = useUI(s => s.saveMeeting);
  const setView    = useUI(s => s.setView);

  const employees  = useRH(s => s.employees);
  const eff        = useRH(s => s.effectiveSalary);
  const apiKey     = useSettings(s => s.anthropicKey);
  const model      = useSettings(s => s.model);

  const [topic, setTopic] = useState("Plano de ataque para o próximo trimestre");
  const [agenda, setAgenda] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [useAI, setUseAI] = useState(true);
  const [section, setSection] = useState<"meeting" | "builder" | "brainstorm">("meeting");
  const [builderClient, setBuilderClient] = useState<string>("");
  const posterRef = useRef<HTMLDivElement>(null);
  const [suggesting, setSuggesting] = useState(false);
  const [suggestions, setSuggestions] = useState<{ personaId: string; reason: string }[]>([]);

  const setMeetingPicks = (ids: string[]) => {
    useUI.setState({ meetingPicks: ids });
  };

  const suggestAttendees = async () => {
    if (!topic.trim()) { alert("Digita o assunto da reunião primeiro."); return; }
    if (!apiKey) { alert("Configure chave Anthropic em ⚙️."); return; }
    const hired = Object.values(employees).filter(e => e.status === "hired").map(e => e.personaId);
    if (hired.length === 0) { alert("Sem agentes contratados. Vai no RH primeiro."); return; }
    setSuggesting(true);
    try {
      const sug = await suggestMeetingAttendeesAI({
        apiKey, model, topic, mode, hiredAgentIds: hired
      });
      if (sug.length === 0) { alert("Claude não recomendou ninguém. Reveja o tópico."); return; }
      setSuggestions(sug);
      setMeetingPicks(sug.map(s => s.personaId));
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setSuggesting(false);
    }
  };

  const exportPoster = async () => {
    if (agenda.length === 0) { alert("Gere a pauta primeiro."); return; }
    if (!posterRef.current) return;
    await snapshotElement(posterRef.current, `vpc-reuniao-${new Date().toISOString().slice(0,10)}.png`);
  };

  const [extractLoading, setExtractLoading] = useState(false);
  const [extracted, setExtracted] = useState<{ title: string; description: string; targetDepts: string[]; priority: string }[]>([]);
  const createTask = useTasks(s => s.create);

  const extractTasks = async () => {
    if (agenda.length === 0) { alert("Gere a pauta primeiro."); return; }
    if (!apiKey) { alert("Configure a chave Anthropic em ⚙️."); return; }
    setExtractLoading(true);
    try {
      const tasks = await extractTasksAI({
        apiKey, model, topic, agenda: agenda.join("\n")
      });
      setExtracted(tasks);
      if (tasks.length === 0) alert("Claude não conseguiu extrair tarefas claras dessa ata.");
    } catch (e) {
      alert("Falha: " + (e as Error).message);
    } finally {
      setExtractLoading(false);
    }
  };

  const commitTasks = (which: number[]) => {
    const valid = which.map(i => extracted[i]).filter(Boolean);
    valid.forEach(t => {
      createTask({
        title: t.title,
        description: t.description,
        targetDepts: t.targetDepts,
        assignedTo: [],
        priority: (["low","normal","high","urgent"].includes(t.priority) ? t.priority : "normal") as any,
        tags: [`meeting:${topic.slice(0,30)}`],
        status: "pending"
      });
    });
    alert(`${valid.length} demanda${valid.length === 1 ? "" : "s"} criada${valid.length === 1 ? "" : "s"}.`);
    setExtracted([]);
  };

  const personas = picks
    .map(id => PERSONAS.find(p => p.id === id))
    .filter((p): p is NonNullable<typeof p> => Boolean(p))
    .filter(p => employees[p.id]?.status === "hired");

  const totalCost  = personas.reduce((a, p) => a + eff(p.id), 0);
  const totalPower = personas.reduce((a, p) => a + p.power, 0);

  const generateAta = () => {
    const items: string[] = [];
    items.push(`Pauta: ${topic}`);
    items.push(`Sala convocada — ${personas.length} participantes · custo proporcional: USD ${Math.round(totalCost / 160).toLocaleString("en-US")}/hora`);
    items.push("");
    const byDept: Record<string, typeof personas> = {};
    personas.forEach(p => {
      const [d] = p.slotId.split(":");
      (byDept[d] ||= []).push(p);
    });
    Object.entries(byDept).forEach(([deptId, ps]) => {
      const dept = ALL_DEPTS.find(d => d.id === deptId);
      items.push(`▸ ${dept?.name ?? deptId.toUpperCase()}`);
      ps.forEach(p => {
        const hint = ATA_LINE(p.id);
        items.push(`   — ${p.name}: ${hint ?? `traz o ângulo do seu departamento sobre "${topic}".`}`);
      });
    });
    items.push("");
    items.push("▸ DECISÃO FINAL");
    if (personas.find(p => p.id === "trump")) {
      const consigliere = personas.find(p => p.id === "marcio");
      items.push(`   — Marcio (consigliere) sussurra o veredito${consigliere ? "" : " — ausente, decisão pode sair errada"}.`);
      items.push("   — Trump bate o martelo: a última canetada.");
    } else {
      items.push("   — Sem Trump na sala, ninguém fecha. Contrate-o para decisões finais.");
    }
    setAgenda(items);
  };

  const generateConselho = () => {
    const items: string[] = [];
    items.push(`Conselho ao Marcio sobre: ${topic}`);
    items.push(`${personas.length} conselheiros à mesa — cada um te entrega uma diretriz.`);
    items.push("");
    personas.forEach(p => {
      items.push(`▸ ${p.name.toUpperCase()}`);
      items.push(`   ${CONSELHO_LINE(p.id, topic)}`);
      items.push("");
    });
    items.push("▸ SÍNTESE");
    items.push("   Marcio: pesa os conselhos, dorme com a decisão, e amanhã sussurra a resposta ao Trump.");
    if (personas.find(p => p.id === "trump")) {
      items.push("   Trump: dá a última canetada — sim, não, ou \"refaça maior\".");
    }
    setAgenda(items);
  };

  const generateOffline = () => {
    if (mode === "conselho") generateConselho();
    else generateAta();
  };

  const generate = async () => {
    if (!useAI || !apiKey) {
      generateOffline();
      return;
    }
    if (personas.length === 0) {
      alert("Convoque ao menos 1 participante.");
      return;
    }
    try {
      setLoading(true);
      setAgenda([]);
      await generateAgendaAIStream(
        { apiKey, model, topic, mode, personaIds: personas.map(p => p.id) },
        (textSoFar) => setAgenda(textSoFar.split("\n"))
      );
    } catch (e) {
      console.error(e);
      alert("Falha na API: " + (e as Error).message + "\nUsando modo offline.");
      generateOffline();
    } finally {
      setLoading(false);
    }
  };

  const onSave = () => {
    if (agenda.length === 0) {
      alert("Gere a pauta primeiro.");
      return;
    }
    const title = prompt("Título desta reunião:", topic.slice(0, 60)) || topic.slice(0, 60);
    saveMtg({ title, topic, mode, picks: personas.map(p => p.id), agenda });
    alert("Reunião salva. Veja em 'Reuniões salvas'.");
  };

  return (
    <div className="px-8 pb-16">
      {/* SUB-TABS */}
      <div className="flex border-2 border-ink mb-4">
        {([
          ["meeting","🪑 reunião · pauta"],
          ["brainstorm","🎨 brainstorm criativo"],
          ["builder","📝 criar tarefas"]
        ] as const).map(([id, label]) => (
          <button key={id} onClick={() => setSection(id)}
            className={`flex-1 px-4 py-2 font-display font-bold uppercase border-r last:border-r-0 border-ink
              ${section === id ? "bg-ink text-bone" : "bg-bone text-ink hover:bg-ink/10"}`}>
            {label}
          </button>
        ))}
      </div>

      {section === "brainstorm" && (
        <div className="mb-4">
          <MeetingBrainstorm />
        </div>
      )}

      {section === "builder" && (
        <div className="mb-4">
          <div className="border-2 border-ink bg-bone p-3 mb-3 flex items-end justify-between gap-3 flex-wrap">
            <div>
              <div className="tag text-graydk">cliente padrão das tarefas</div>
              <select value={builderClient} onChange={(e) => setBuilderClient(e.target.value)}
                className="border border-ink bg-bone px-2 py-1 font-mono text-[12px]">
                <option value="">— interno —</option>
                {CLIENTS.map(c => <option key={c.id} value={c.id}>{c.shortName}</option>)}
              </select>
            </div>
            <p className="font-crimson italic text-paper text-sm flex-1 min-w-[200px]">
              Marcio: digita as tarefas como num bloco de notas. Usa <span className="font-mono not-italic">🪄 IA</span> pra refinar título/briefing, anexa referência, estima horas, e clica "enviar pro Quadro".
            </p>
          </div>
          <MeetingTaskBuilder defaultClientId={builderClient} />
        </div>
      )}

      {section === "meeting" && (
      <>
      <header className={`border-2 border-ink text-bone px-5 py-4 mb-4 flex items-end justify-between
        ${mode === "conselho" ? "bg-gold text-ink" : "bg-red"}`}>
        <div>
          <div className={`font-mono text-xs ${mode === "conselho" ? "text-ink/70" : "text-bone/80"}`}>
            {mode === "conselho" ? "CONSELHO-01" : "SALA-01"}
          </div>
          <h2 className="font-display font-black uppercase text-4xl leading-none">
            {mode === "conselho" ? "CONSELHO AO MARCIO" : "SALA DE REUNIÃO"}
          </h2>
          <div className={`font-serif italic mt-1 ${mode === "conselho" ? "text-ink/70" : "text-bone/80"}`}>
            {personas.length} participantes · USD {totalCost.toLocaleString("en-US")}/mês na mesa · poder somado {totalPower}
          </div>
        </div>
        <div className="flex flex-col gap-2 items-end">
          <div className="flex border-2 border-ink">
            <button
              onClick={() => setMode("ata")}
              className={`tag px-3 py-1.5 border-r border-ink ${mode === "ata" ? "bg-ink text-bone" : "bg-bone text-ink"}`}>
              modo ata
            </button>
            <button
              onClick={() => setMode("conselho")}
              className={`tag px-3 py-1.5 ${mode === "conselho" ? "bg-ink text-bone" : "bg-bone text-ink"}`}>
              modo conselho
            </button>
          </div>
          <label className="flex items-center gap-2 cursor-pointer tag">
            <input type="checkbox" checked={useAI} onChange={(e) => setUseAI(e.target.checked)} />
            usar Claude API {!apiKey && <span className="text-red">(sem chave)</span>}
          </label>
          <div className="flex gap-2">
            <button
              disabled={loading}
              onClick={generate}
              className="btn bg-ink text-bone border-ink disabled:opacity-50">
              {loading ? "gerando…" : (useAI && apiKey ? "🤖 gerar c/ IA" : "▸ gerar")}
            </button>
            {agenda.length > 0 && (
              <button onClick={onSave} className="btn bg-bone text-ink border-ink">↳ salvar</button>
            )}
            {agenda.length > 0 && (
              <button onClick={exportPoster} className="btn bg-bone text-ink border-ink">📰 pôster</button>
            )}
            {agenda.length > 0 && (
              <button onClick={extractTasks} disabled={extractLoading}
                      className="btn bg-bone text-ink border-ink disabled:opacity-50">
                {extractLoading ? "extraindo…" : "↳ virar demandas"}
              </button>
            )}
            {picks.length > 0 && (
              <button onClick={clearAll} className="btn bg-bone text-ink border-ink">limpar</button>
            )}
            <button onClick={() => setView("meetings")} className="btn bg-bone text-ink border-ink">arquivo →</button>
          </div>
        </div>
      </header>

      <div className="border-2 border-ink p-4 mb-4 bg-bone">
        <div className="flex items-center justify-between mb-2">
          <div className="tag text-graydk">tópico da reunião</div>
          <button
            onClick={suggestAttendees}
            disabled={suggesting || !topic.trim()}
            className="btn bg-red text-bone border-red disabled:opacity-40 disabled:cursor-not-allowed">
            {suggesting ? "🤖 convocando…" : "🤖 IA convocar time"}
          </button>
        </div>
        <input
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          placeholder="digite o assunto — ex: 'lançamento Black Friday Mentoria VPC com R$200k de investimento'"
          className="w-full bg-transparent outline-none font-display font-bold text-2xl uppercase placeholder:text-graymd placeholder:normal-case placeholder:italic placeholder:text-base placeholder:font-serif"
        />

        {suggestions.length > 0 && (
          <div className="mt-4 border-t border-ink/30 pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="tag text-graydk">
                🤖 {suggestions.length} convocados pela IA · clique pra remover
              </div>
              <button
                onClick={() => { setSuggestions([]); setMeetingPicks([]); }}
                className="tag text-red underline">limpar tudo</button>
            </div>
            <div className="grid md:grid-cols-2 gap-1.5">
              {suggestions.map(s => {
                const p = PERSONAS.find(x => x.id === s.personaId);
                if (!p) return null;
                return (
                  <button
                    key={s.personaId}
                    onClick={() => {
                      const next = picks.filter(id => id !== s.personaId);
                      setMeetingPicks(next);
                      setSuggestions(suggestions.filter(x => x.personaId !== s.personaId));
                    }}
                    className="text-left border-2 border-ink p-2 bg-bone hover:bg-red/10 group"
                    title="clique pra remover da reunião"
                  >
                    <div className="flex items-center gap-2">
                      <div className="w-9 h-9 flex items-center justify-center font-display font-black text-xs shrink-0 overflow-hidden"
                           style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
                        {employees[p.id]?.avatarUrl
                          ? <img src={employees[p.id]!.avatarUrl} alt="" className="w-full h-full object-cover" />
                          : initials(p.name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-display font-bold uppercase text-sm leading-tight truncate">
                          {p.name}
                        </div>
                        <div className="font-crimson italic text-paper text-[12px] leading-tight">
                          {s.reason}
                        </div>
                      </div>
                      <span className="text-red text-lg opacity-0 group-hover:opacity-100">×</span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-6">
        {personas.length === 0 && (
          <div className="md:col-span-2 xl:col-span-3 border-2 border-graymd p-10 text-center font-serif italic text-paper">
            Nenhum membro convocado. Volte ao RH e clique <span className="font-mono not-italic">+ reunião</span> nos cards de contratados.
          </div>
        )}
        {personas.map(p => (
          <div key={p.id} className="border-2 border-ink p-3 bg-bone flex gap-3 items-start">
            <div
              className="shrink-0 w-14 h-14 flex items-center justify-center font-display font-black text-xl"
              style={{ background: monogramColor(p.id), color: "#F2EEE6" }}>
              {initials(p.name)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-bold uppercase text-xl truncate">{p.name}</div>
              <div className="font-crimson italic text-sm text-paper line-clamp-2">{p.bio}</div>
              <div className="mt-1 tag text-graydk">poder {p.power}/10 · USD {eff(p.id).toLocaleString("en-US")}/mês</div>
            </div>
            <button onClick={() => toggle(p.id)} className="btn btn-fire shrink-0">remover</button>
          </div>
        ))}
      </div>

      {/* pôster offscreen pra snapshot */}
      {agenda.length > 0 && (
        <div style={{ position: "fixed", left: -10000, top: 0, pointerEvents: "none" }} aria-hidden="true">
          <MeetingPoster
            ref={posterRef}
            title={topic}
            topic={topic}
            mode={mode}
            agenda={agenda}
            personaIds={personas.map(p => p.id)}
          />
        </div>
      )}

      {extracted.length > 0 && (
        <div className="border-2 border-gold bg-gold/10 mb-4">
          <div className="bg-gold text-ink px-4 py-2 flex items-center justify-between">
            <span className="font-display uppercase font-bold">↳ tarefas extraídas pelo Claude</span>
            <div className="flex gap-2">
              <button onClick={() => commitTasks(extracted.map((_, i) => i))}
                      className="btn bg-ink text-bone border-ink">criar todas ({extracted.length})</button>
              <button onClick={() => setExtracted([])} className="btn bg-bone text-ink border-bone">descartar</button>
            </div>
          </div>
          <div className="p-3 space-y-2">
            {extracted.map((t, i) => (
              <div key={i} className="border border-ink bg-bone p-3 flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-display font-bold uppercase">{t.title}</span>
                    <span className="tag bg-ink text-bone px-1.5">{t.priority}</span>
                    {t.targetDepts.map(d => (
                      <span key={d} className="tag bg-graydk text-bone px-1.5">{d}</span>
                    ))}
                  </div>
                  <p className="font-crimson italic text-paper text-sm">{t.description}</p>
                </div>
                <button onClick={() => commitTasks([i])} className="btn btn-hire shrink-0">+ criar</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* agenda display */}
      {agenda.length > 0 && (
        <div className="border-2 border-ink bg-bone">
          <div className="bg-ink text-bone px-4 py-2 flex items-center justify-between">
            <span className="font-display uppercase font-bold text-lg">
              {mode === "conselho" ? "Conselho da Mesa" : "Ata da Reunião"}
            </span>
            <button
              onClick={() => navigator.clipboard.writeText(agenda.join("\n"))}
              className="tag underline">copiar</button>
          </div>
          <pre className="p-4 font-mono text-[13px] whitespace-pre-wrap leading-relaxed">
{agenda.join("\n")}{loading && <span className="inline-block w-2 h-4 bg-red ml-0.5 animate-pulse align-middle" />}
          </pre>
        </div>
      )}
      </>
      )}
    </div>
  );
}
